/**
 * 出口合规检测统一混入（导出 / 分享 / 进入下一步等动作的前置检测）。
 *
 * 使用方式：
 *   1) 页面引入并注册：mixins: [exportCompliance]，模板中加入
 *      <ExportComplianceDialog :visible.sync="complianceDialog.visible" ... />（组件已由本混入注册）；
 *   2) 在执行动作前调用：this.runExportCompliance(projectId, (detectionId) => { 原动作 });
 *      分享 / 导出类动作须把 detectionId 回传后端（服务端做检测-动作绑定校验，防绕过前端）；
 *      进入下一步等非分享 / 导出动作可忽略该参数。
 *
 * 结果处理（与第三步按钮保持一致）：
 *   - 0-1 级 / 审查已办结通过 / 2 级确认 → 执行传入的后续动作（携带本次检测标识）；
 *   - 检测异常人工放行 → 先调留痕接口登记（服务端二次校验据此放行），成功后再执行后续动作；
 *   - 审查办理中 → 提示已提交审查并中断；
 *   - 审查未通过 / >2 级 → 弹窗，可发起审查（提交成功后返回报价单列表）；
 *   - 检测异常 → 弹窗提示系统异常，可人工放行或取消。
 */
import API from '@/api';
import ExportComplianceDialog from '@/views/components/ExportComplianceDialog';

export default {
  components: { ExportComplianceDialog },
  data () {
    return {
      complianceDialog: {
        visible: false,
        mode: 'confirm',
        level: null,
        conclusion: '',
        details: []
      },
      complianceChecking: false,
      // 当前检测的报价单号（发起审查时使用）
      complianceProjectId: '',
      // 本次检测标识（分享/导出时回传，服务端做检测-动作绑定校验）
      complianceDetectionId: '',
      // 检测通过后待执行的后续动作
      compliancePassHandler: null
    };
  },
  methods: {
    /**
     * 出口合规检测：按结果决定是否执行 onPass（通过放行的后续动作）。
     *
     * @param {String} projectId 报价单号
     * @param {Function} onPass 放行后执行的后续动作
     */
    runExportCompliance (projectId, onPass) {
      // 检测中或风险弹窗未关闭时，忽略重复触发，避免并发检测与回调覆盖
      if (this.complianceChecking || this.complianceDialog.visible) {
        return;
      }
      this.complianceChecking = true;
      this.complianceProjectId = projectId;
      this.compliancePassHandler = onPass;
      API.exportComplianceDetect({ projectId: projectId }).then((res) => {
        let json = res.data;
        if (json.status !== '200' || !json.data) {
          this.complianceChecking = false;
          this.complianceDetectionId = '';
          this.showComplianceError();
          return;
        }
        let data = json.data;
        // 记录本次检测标识：检测异常时同样返回（用于人工放行留痕与后续动作绑定）
        this.complianceDetectionId = data.detectionId || '';
        if (data.detectStatus === 'ERROR') {
          this.complianceChecking = false;
          this.showComplianceError();
          return;
        }
        let reviewInfo = data.reviewInfo;
        if (reviewInfo && reviewInfo.hasReview) {
          // 已有待办理审查单：提示已提交审查，停留当前页
          if (reviewInfo.handleStatus === 'PENDING') {
            this.complianceChecking = false;
            this.compliancePassHandler = null;
            this.messageHints(`${this.$t('exportCompliance.reviewPending')}`, 'warning');
            return;
          }
          // 审查未通过：拦截，允许再次发起审查（结论展示本次检测 resultMsg，不被审查单结论覆盖）
          if (reviewInfo.handleResult === 2) {
            this.complianceChecking = false;
            this.showComplianceDialog('review', data);
            return;
          }
          // 审查已办结且通过：直接放行，不再按本次检测等级重复拦截（放行仅本次有效）
          if (reviewInfo.handleResult === 1) {
            this.executeCompliancePass();
            return;
          }
        }
        let level = data.riskLevel;
        if (level === null || level === undefined || level <= 1) {
          // 0-1 级：放行
          this.executeCompliancePass();
        } else if (level === 2) {
          this.complianceChecking = false;
          this.showComplianceDialog('confirm', data);
        } else {
          this.complianceChecking = false;
          this.showComplianceDialog('review', data);
        }
      }).catch(() => {
        this.complianceChecking = false;
        this.complianceDetectionId = '';
        this.showComplianceError();
      });
    },

    // 放行：执行调用方传入的后续动作，并回传本次检测标识
    executeCompliancePass () {
      this.complianceChecking = false;
      let handler = this.compliancePassHandler;
      let detectionId = this.complianceDetectionId;
      this.compliancePassHandler = null;
      if (handler) {
        // 分享/导出请求需携带检测标识，服务端据此做检测-动作绑定校验
        handler(detectionId);
      }
    },

    // 检测异常：提示系统异常，用户可选择继续或取消
    showComplianceError () {
      this.complianceDialog = {
        visible: true,
        mode: 'error',
        level: null,
        conclusion: this.$t('exportCompliance.systemError'),
        details: []
      };
    },

    showComplianceDialog (mode, data) {
      this.complianceDialog = {
        visible: true,
        mode: mode,
        level: data.riskLevel === undefined ? null : data.riskLevel,
        conclusion: data.conclusion || '',
        details: data.details || []
      };
    },

    // 取消：停留在当前页面，放弃后续动作
    onComplianceCancel () {
      this.complianceDialog.visible = false;
      this.compliancePassHandler = null;
    },

    // 2 级确认 / 异常人工放行
    onComplianceConfirm () {
      // 异常人工放行：须先落库留痕（服务端二次校验据此放行）
      if (this.complianceDialog.mode === 'error') {
        this.submitManualRelease();
        return;
      }
      this.complianceDialog.visible = false;
      this.executeCompliancePass();
    },

    // 人工放行留痕：操作人由服务端取当前登录用户，成功后携带检测标识继续后续动作
    submitManualRelease () {
      this.complianceChecking = true;
      API.exportComplianceManualRelease({
        projectId: this.complianceProjectId,
        detectionId: this.complianceDetectionId
      }).then((res) => {
        this.complianceChecking = false;
        let json = res.data;
        if (json.status === '200' && json.data) {
          // 服务端可能补建异常记录并返回新的检测标识
          this.complianceDetectionId = json.data;
          this.complianceDialog.visible = false;
          this.executeCompliancePass();
        } else {
          // 留痕失败时保持弹窗，允许重试或取消（未留痕的分享/导出将被服务端拦截）
          let msg = typeof json.data === 'string' && json.data
            ? json.data
            : this.$t('exportCompliance.releaseFailed');
          this.messageHints(`${msg}`, 'error');
        }
      }).catch(() => {
        this.complianceChecking = false;
        this.messageHints(`${this.$t('exportCompliance.releaseFailed')}`, 'error');
      });
    },

    // 发起审查：提交成功后返回价格单列表
    onComplianceSubmitReview () {
      this.complianceDialog.visible = false;
      this.compliancePassHandler = null;
      this.complianceChecking = true;
      API.exportComplianceReviewSubmit({ projectId: this.complianceProjectId }).then((res) => {
        this.complianceChecking = false;
        let json = res.data;
        if (json.status === '200') {
          // 成功提示优先使用接口返回的 promptMessage，缺失时回退默认文案
          let msg = json.data || this.$t('exportCompliance.reviewSubmitted');
          this.messageHints(`${msg}`, 'success');
          this.$router.push({ path: '/main' });
        } else {
          this.messageHints(`${this.$t('exportCompliance.reviewSubmitFailed')}`, 'error');
        }
      }).catch(() => {
        this.complianceChecking = false;
        this.messageHints(`${this.$t('exportCompliance.reviewSubmitFailed')}`, 'error');
      });
    },

    messageHints (title, type) {
      this.$notify({
        title: title,
        type: type
      });
    }
  }
};
